import { useEffect, useRef, useState } from "react";
import type { ApprovedCodeView } from "../types";

interface Props {
  resinLabel: string;
  baseCodes: ApprovedCodeView[];
  designationCountByBase: Map<string, number>;
  totalDesignationCount: number;
  selectedBaseCode: string | null;
  onSelect: (baseCode: string | null) => void;
}

const OPTION_CLASSES =
  "grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-2 rounded bg-transparent border-0 px-2 py-1.5 text-left text-[12.5px] cursor-pointer hover:bg-rs-jet";

export function BaseCodeSelect({
  resinLabel,
  baseCodes,
  designationCountByBase,
  totalDesignationCount,
  selectedBaseCode,
  onSelect,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    searchInputRef.current?.focus();
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const normalizedSearch = searchText.trim().toLowerCase();
  const matchingBaseCodes = baseCodes.filter(
    (baseCode) =>
      !normalizedSearch ||
      baseCode.code.toLowerCase().includes(normalizedSearch) ||
      baseCode.readsAs.toLowerCase().includes(normalizedSearch),
  );

  const choose = (baseCode: string | null) => {
    onSelect(baseCode);
    setIsOpen(false);
    setSearchText("");
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-[5px] border bg-rs-black px-2.5 py-1.75 text-[13px] text-rs-off-white cursor-pointer hover:border-rs-green ${
          isOpen ? "border-rs-green" : "border-[#3c4235]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen((wasOpen) => !wasOpen);
          setSearchText("");
        }}
      >
        Base: <b className="font-semibold text-rs-green">{selectedBaseCode ?? "All"}</b>
        <span className="text-[11px] text-[#a9b0a1]" aria-hidden="true">
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full z-30 mt-1 flex w-[min(360px,86vw)] flex-col gap-1.5 rounded-lg border border-[#3c4235] bg-rs-black p-2 shadow-[0_14px_34px_rgba(0,0,0,0.55)]">
          <input
            ref={searchInputRef}
            className="ctl text-[13px] py-1.5"
            placeholder="Type a base code or name…"
            aria-label="Search base codes"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <ul className="m-0 max-h-70 list-none overflow-y-auto p-0" role="listbox" aria-label={`${resinLabel} base codes`}>
            {!normalizedSearch && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedBaseCode === null}
                  className={`${OPTION_CLASSES} font-semibold text-rs-off-white ${selectedBaseCode === null ? "bg-rs-green/10" : ""}`}
                  onClick={() => choose(null)}
                >
                  <span />
                  <span>All base codes</span>
                  <span className="tabular-nums">{totalDesignationCount}</span>
                </button>
              </li>
            )}
            {matchingBaseCodes.map((baseCode) => {
              const isSelected = baseCode.code === selectedBaseCode;
              return (
                <li key={baseCode.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`${OPTION_CLASSES} ${isSelected ? "bg-rs-green/10 text-rs-off-white" : "text-[#a9b0a1]"}`}
                    onClick={() => choose(baseCode.code)}
                  >
                    <code className="text-xs text-rs-green">{baseCode.code}</code>
                    <span className="truncate">{baseCode.readsAs}</span>
                    <span className="tabular-nums text-rs-off-white">{designationCountByBase.get(baseCode.code) ?? 0}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {normalizedSearch && matchingBaseCodes.length === 0 && (
            <p className="m-1 text-[12.5px] text-[#a9b0a1]">No base codes match "{searchText.trim()}".</p>
          )}
        </div>
      )}
    </div>
  );
}
